import React, { useState, useContext, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import {
    Mic,
    Headphones,
    User,
    Menu,
    X,
    GraduationCap,
    ChevronRight,
    Wand2,
    BriefcaseBusiness,
    Compass,
    LayoutDashboard
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { PlansContext } from '../../App';
import styles from './Header.module.css';
import NotificationsIcon from '../Notifications/NotificationsIcon';
import StreakIcon from '../Streak/StreakIcon';
import LevelBadge from '../UserLevel/LevelBadge';
import { useLocation } from 'react-router-dom';

const Header = ({ assessmentCompleted }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { user } = useAuth();
    const { handleLimitReached } = useContext(PlansContext);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 950);
    const headerRef = useRef(null);
    const location = useLocation();
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 950);
        };

        const handleClickOutside = (event) => {
            if (headerRef.current &&
                !headerRef.current.contains(event.target) &&
                isMenuOpen &&
                isMobile) {
                setIsMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isMenuOpen, isMobile]);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handlePlanClick = () => {
        handleLimitReached();
    };

    const getPlanBadgeContent = () => {
        switch (user?.plan?.plan) {
            case 'beginner':
                return (
                    <>
                        <Compass size={14} />
                        <span className={styles.planText}>Beginner</span>
                    </>
                );
            case 'pro':
                return (
                    <>
                        <BriefcaseBusiness size={14} />
                        <span className={styles.planText}>Professional</span>
                    </>
                );
            case 'student':
                return (
                    <>
                        <GraduationCap size={14} />
                        <span className={styles.planText}>Student</span>
                    </>
                );
            default:
                return (
                    <>
                        <span className={styles.planText}>Upgrade Plan</span>
                        <ChevronRight size={14} className={styles.upgradeArrow} />
                    </>
                );
        }
    };

    if (location.pathname === '/assessment' && !assessmentCompleted) {
        return null;
    }

    return (
        <header className={styles.header} ref={headerRef}>
            <nav className={styles.navbar}>
                <div className={styles.logo}>TopSpeaks</div>
                {isMobile && (
                    <div className={styles.mobileControls}>
                        <LevelBadge />
                        <StreakIcon />
                        <NotificationsIcon />
                        <button className={styles.menuButton} onClick={toggleMenu}>
                            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                )}

                <div className={`${styles.navContent} ${isMenuOpen ? styles.menuOpen : ''}`}>
                    <div className={styles.mainNav}>
                        <NavLink to="/dashboard" className={({ isActive }) => isActive ? styles.active : ''}>
                            <LayoutDashboard size={18} /> Dashboard
                        </NavLink>
                        <NavLink to="/practice" className={({ isActive }) => isActive ? styles.active : ''}>
                            <Mic size={18} /> Practice
                        </NavLink>
                        <NavLink to="/create" className={({ isActive }) => isActive ? styles.active : ''}>
                            <Wand2 size={18} /> Create
                        </NavLink>
                        <NavLink to="/recordings" className={({ isActive }) => isActive ? styles.active : ''}>
                            <Headphones size={18} /> Recordings
                        </NavLink>
                    </div>
                    <div className={styles.userNav}>
                        {!isMobile && <LevelBadge />}
                        {!isMobile && <StreakIcon />}
                        {!isMobile && <NotificationsIcon />}
                        <button
                            onClick={handlePlanClick}
                            className={`${styles.planBadge} ${user?.plan?.plan === 'pro'
                                ? styles.enterprisePlan
                                : user?.plan?.plan === 'student'
                                    ? styles.academicPlan
                                    : styles.standardPlan
                                }`}
                        >
                            {getPlanBadgeContent()}
                        </button>
                        <NavLink to="/account" className={({ isActive }) => isActive ? styles.active : ''}>
                            <User size={18} /> Account
                        </NavLink>
                    </div>
                </div>
            </nav>
        </header>
    );
};

export default Header;